import { QueryKey, useMutation } from '@tanstack/react-query';

import { queryClient } from 'index';
import { postToast } from 'features/common/utils/postFlash';
import { getApiErrorMessage } from './apiError';

interface UseToastMutationConfig<TVars> {
	mutationFn: (vars: TVars) => Promise<unknown>;
	/** Success-toast copy shown when the mutation resolves. */
	successMessage: string;
	/** Fallback error-toast copy; the API message wins when present (see getApiErrorMessage). */
	errorMessage: string;
	/** Query keys invalidated on success so dependent views refetch. */
	invalidateKeys?: QueryKey[];
	/** Caller hook to run after the success toast + invalidations (e.g. close a modal). */
	onSuccess?: () => void;
}

/**
 * The single mutation wrapper for this feature: a success toast + invalidations
 * on resolve, and an API-message-or-fallback error toast on reject. Routing all
 * mutations through here keeps the toast/invalidate contract in one place.
 */
export const useToastMutation = <TVars>({
	mutationFn,
	successMessage,
	errorMessage,
	invalidateKeys = [],
	onSuccess,
}: UseToastMutationConfig<TVars>) =>
	useMutation({
		mutationFn,
		onSuccess: async () => {
			postToast('success', successMessage);
			await Promise.all(invalidateKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			postToast('error', getApiErrorMessage(error, errorMessage));
		},
	});
